import { BasePrinterAdapter } from './BasePrinterAdapter'
import type {
  PrinterConnectionConfig,
  PrinterTelemetry,
  PrinterCapabilities,
  CameraStream,
  BambuState,
} from '../printerConnection.types'

export class BambuAdapter extends BasePrinterAdapter {
  private ws: WebSocket | null = null
  private mqttSequenceId = 0

  constructor(config: PrinterConnectionConfig) {
    super(config)
  }

  async connect(): Promise<void> {
    this.setConnectionStatus('connecting')

    try {
      // Bambu uses MQTT over WebSocket or cloud API
      // For local connection, we use the printer's built-in MQTT
      if (this.config.accessCode && this.config.serial) {
        await this.connectLocal()
      } else {
        // Cloud API connection would require OAuth - for now show error
        throw new Error('Bambu Cloud connection requires OAuth setup. Use local connection with access code.')
      }
    } catch (error) {
      this.state.lastError = error instanceof Error ? error.message : 'Connection failed'
      this.setConnectionStatus('error')
      throw error
    }
  }

  private async connectLocal(): Promise<void> {
    // Bambu printers expose MQTT on port 8883 (TLS) or via local API
    // Using WebSocket proxy approach for browser compatibility
    // Note: wsUrl would be used when backend MQTT proxy is available
    // const wsUrl = this.buildWsUrl('/mqtt')
    
    // For browser-based connection, we need a WebSocket proxy
    // In production, this would go through a backend service
    // For now, we'll simulate the connection and use polling
    
    this.state.firmwareInfo = {
      name: 'Bambu Lab',
      version: 'Local',
    }

    this.state.capabilities = await this.getCapabilities()
    this.setConnectionStatus('connected')
    this.state.printerStatus = 'idle'
    
    // Start polling for status (in production, MQTT would push updates)
    this.startPolling()
  }

  async disconnect(): Promise<void> {
    this.stopPolling()
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.setConnectionStatus('disconnected')
    this.state.printerStatus = 'offline'
  }

  protected parseBambuStatus(data: BambuState): void {
    const print = data.print

    // Update printer status
    switch (print.gcode_state) {
      case 'RUNNING':
        this.state.printerStatus = 'printing'
        break
      case 'PAUSE':
        this.state.printerStatus = 'paused'
        break
      case 'IDLE':
      case 'FINISH':
        this.state.printerStatus = 'idle'
        break
      case 'FAILED':
        this.state.printerStatus = 'error'
        break
    }

    this.state.currentFile = print.subtask_name || print.gcode_file

    const telemetry: PrinterTelemetry = {
      timestamp: new Date(),
      temperatures: {
        nozzle: {
          actual: print.nozzle_temper,
          target: print.nozzle_target_temper,
        },
        bed: {
          actual: print.bed_temper,
          target: print.bed_target_temper,
        },
        chamber: {
          actual: print.chamber_temper,
          target: 0, // Bambu doesn't have chamber heating control
        },
      },
      printProgress: print.mc_percent,
      printTimeRemaining: print.mc_remaining_time * 60, // Convert to seconds
      layerInfo: {
        current: print.layer_num,
        total: print.total_layer_num,
      },
      fanSpeed: parseInt(print.cooling_fan_speed) || 0,
    }

    this.updateTelemetry(telemetry)
  }

  async getTelemetry(): Promise<PrinterTelemetry> {
    // In a real implementation, this would query the printer's local API
    // or receive MQTT updates. For demo purposes, return current state.
    if (this.state.telemetry) {
      return this.state.telemetry
    }

    // Return default telemetry
    return {
      timestamp: new Date(),
      temperatures: {
        nozzle: { actual: 25, target: 0 },
        bed: { actual: 25, target: 0 },
        chamber: { actual: 25, target: 0 },
      },
    }
  }

  async getCapabilities(): Promise<PrinterCapabilities> {
    // Detect printer model from serial number prefix
    const serial = this.config.serial || ''
    const isX1 = serial.startsWith('00M') || serial.startsWith('00W')
    const isP1 = serial.startsWith('00P') || serial.startsWith('01P')
    const isA1 = serial.startsWith('030') || serial.startsWith('039')

    if (isX1) {
      return {
        hasCamera: true,
        hasEnclosure: true,
        hasMultiMaterial: true, // AMS support
        hasAutoLeveling: true,
        hasFilamentSensor: true,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 110,
        buildVolume: { x: 256, y: 256, z: 256 },
      }
    } else if (isP1) {
      return {
        hasCamera: true,
        hasEnclosure: true,
        hasMultiMaterial: true,
        hasAutoLeveling: true,
        hasFilamentSensor: true,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 100,
        buildVolume: { x: 256, y: 256, z: 256 },
      }
    } else if (isA1) {
      return {
        hasCamera: true,
        hasEnclosure: false,
        hasMultiMaterial: true, // AMS lite
        hasAutoLeveling: true,
        hasFilamentSensor: true,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 100,
        buildVolume: { x: 256, y: 256, z: 256 },
      }
    }

    // Default capabilities
    return {
      hasCamera: true,
      hasEnclosure: true,
      hasMultiMaterial: true,
      hasAutoLeveling: true,
      hasFilamentSensor: true,
      hasPowerMonitor: false,
      maxNozzleTemp: 300,
      maxBedTemp: 110,
      buildVolume: { x: 256, y: 256, z: 256 },
    }
  }

  async getCameraStream(): Promise<CameraStream | null> {
    // Bambu printers stream via their cloud or local RTSP
    // Local streaming uses port 6000 for RTSP
    if (this.config.host) {
      return {
        url: `rtsp://${this.config.host}:6000/streaming/live/1`,
        type: 'webrtc', // Would need transcoding for browser
      }
    }
    return null
  }

  async startPrint(filename: string): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'project_file',
        param: `Metadata/plate_1.gcode`,
        subtask_name: filename,
        url: `file:///sdcard/${filename}`,
        timelapse: false,
        bed_leveling: true,
        flow_cali: true,
        vibration_cali: true,
        layer_inspect: false,
      },
    })
    this.state.printerStatus = 'printing'
    this.emitEvent('print_started', { filename })
  }

  async pausePrint(): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'pause',
      },
    })
    this.state.printerStatus = 'paused'
    this.emitEvent('print_paused')
  }

  async resumePrint(): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'resume',
      },
    })
    this.state.printerStatus = 'printing'
    this.emitEvent('print_resumed')
  }

  async cancelPrint(): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'stop',
      },
    })
    this.state.printerStatus = 'idle'
    this.emitEvent('print_cancelled')
  }

  async setNozzleTemp(temp: number): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'gcode_line',
        param: `M104 S${temp}`,
      },
    })
  }

  async setBedTemp(temp: number): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'gcode_line',
        param: `M140 S${temp}`,
      },
    })
  }

  async homeAxes(axes?: ('x' | 'y' | 'z')[]): Promise<void> {
    const axisStr = axes?.map((a) => a.toUpperCase()).join(' ') || ''
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'gcode_line',
        param: `G28 ${axisStr}`.trim(),
      },
    })
  }

  async jogAxis(axis: 'x' | 'y' | 'z', distance: number): Promise<void> {
    await this.sendCommand({
      print: {
        sequence_id: String(++this.mqttSequenceId),
        command: 'gcode_line',
        param: `G91\nG1 ${axis.toUpperCase()}${distance} F3000\nG90`,
      },
    })
  }

  async listFiles(): Promise<string[]> {
    // Bambu file listing would come from MQTT response
    // For now, return empty - real implementation needs MQTT subscription
    console.warn('Bambu file listing requires MQTT connection')
    return []
  }

  async uploadFile(_file: File, _filename?: string): Promise<void> {
    // Bambu uses FTP for file uploads (port 990 FTPS)
    // Browser can't do FTP directly - needs backend proxy
    console.warn('Bambu file upload requires backend FTP proxy')
    throw new Error('File upload to Bambu printers requires the desktop app or Bambu Studio')
  }

  async deleteFile(_filename: string): Promise<void> {
    // Same FTP limitation
    console.warn('Bambu file deletion requires backend FTP proxy')
    throw new Error('File deletion on Bambu printers requires the desktop app or Bambu Studio')
  }

  private async sendCommand(command: object): Promise<void> {
    // In production, this sends via MQTT
    // For browser demo, we log the command
    console.log('Bambu command:', JSON.stringify(command))
    
    // If we have a WebSocket connection to a backend proxy
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(command))
    }
  }
}
